import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { Icon, type IconName } from '../core/Icon';

export interface MobileNavItem {
  readonly key: string;
  readonly label: string;
  readonly icon: IconName;
}

export interface MobileNavProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly items?: readonly MobileNavItem[];
  readonly active?: string;
  readonly onNavigate?: (key: string) => void;
  /** Contrôles MODE / ÉTAT (`ContextStatus`) — rendus sur une ligne propre sous les destinations,
   * pour rester accessibles sans provoquer de débordement horizontal (voir revue visuelle
   * « corrections visuelles desktop »). */
  readonly trailing?: ReactNode;
  readonly style?: CSSProperties;
}

/** Navigation smartphone : barre basse, quatre destinations, cible 44px minimum. */
export function MobileNav({ items = [], active, onNavigate, trailing, style, ...rest }: MobileNavProps) {
  return (
    <nav
      aria-label="Navigation principale"
      {...rest}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--surface-deep)',
        border: '1px solid rgba(255,255,255,.07)',
        borderRadius: 'var(--radius-panel)',
        padding: '6px 4px',
        boxShadow: 'var(--shadow-card)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-around' }}>
        {items.map((item) => {
          const on = item.key === active;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavigate?.(item.key)}
              aria-current={on ? 'page' : undefined}
              className="ai-nav-item ai-focusable"
              style={{
                flex: 1,
                minHeight: 52,
                display: 'grid',
                justifyItems: 'center',
                alignContent: 'center',
                gap: 6,
                background: 'none',
                border: 0,
                cursor: 'pointer',
                borderRadius: 'var(--r-sm)',
                color: on ? 'var(--accent)' : 'var(--text-faint)',
                transition: 'var(--t-hover)',
              }}
            >
              <Icon name={item.icon} size={20} />
              <span
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--fs-micro)',
                  fontWeight: 500,
                  letterSpacing: 'var(--tracking-label)',
                  textTransform: 'uppercase',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      {trailing && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 10,
            paddingTop: 8,
            borderTop: '1px solid var(--accent-line)',
          }}
        >
          {trailing}
        </div>
      )}
    </nav>
  );
}
